// Conteúdo estratégico — Vivos no Espírito
// Fonte de verdade: Research, Mechanism, Apresentação VnE, Apresentação A,Ê
// Tom: pastoral, sóbrio, reverente, teologicamente responsável

export const siteContent = {
  brand: {
    name: "Acorda, Êutico!",
    tagline: "Desperte para uma fé que transforma",
    logoAlt: "Logo Acorda Êutico"
  },

  hero: {
    headline: "Se sua fé está cansada, não é falta de Deus. É falta de direção.",
    subheadline: "Uma jornada bíblica de 14 dias para cristãos que sentem que algo está desalinhado entre o que creem e o que vivem.",
    supportText: "Para quem ama as Escrituras, mas luta para aplicá-las com clareza e constância no dia a dia.",
    tensionText: "Uma fé que oscila não é apenas desconfortável. É uma fé que ainda não encontrou onde se firmar.",
    ctaText: "Quero viver uma fé desperta"
  },

  problem: {
    title: "Talvez você reconheça algo desta caminhada",
    introduction: "Não é preciso explicar muito. Basta ser honesto consigo mesmo.",
    points: [
      {
        title: "A montanha-russa espiritual",
        description: "Você sai de um congresso ou culto com o coração aquecido. Em poucos dias, a motivação evapora. Volta a apatia. E junto com ela, a pergunta que não quer calar: por que não consigo manter a constância?"
      },
      {
        title: "O cansaço de quem lidera em casa",
        description: "Você chega em casa exausto. Sabe que deveria conduzir um devocional com sua família, mas não consegue — porque você mesmo não entende o que leu. A sensação de estar perdendo a liderança espiritual do seu lar pesa mais do que o cansaço físico."
      },
      {
        title: "A fadiga do espetáculo",
        description: "Os cultos parecem cada vez mais teatrais. Você sente falta da Palavra no meio de tanto barulho. Sente que algo essencial se perdeu — e que o entretenimento gospel está tomando o lugar do alimento sólido."
      },
      {
        title: "O peso da disciplina que falha",
        description: "Você tentou ler cinco capítulos por dia. Falhou no terceiro dia. E carregou a culpa pelo resto da semana, até desistir de vez. Não por falta de desejo — mas por falta de um caminho possível."
      },
      {
        title: "A fé no automático",
        description: "As mesmas orações. Os mesmos gestos. A mesma rotina espiritual que já não produz nada de vivo. Você não abandonou a fé. Mas sente que a fé se tornou uma lista de tarefas mecânicas."
      }
    ],
    conclusion: "Se você se reconheceu em alguma dessas situações, saiba: o problema não é falta de fé. É falta de alicerce. E permanecer numa fé sem fundamento também é uma decisão."
  },

  mechanism: {
    title: "O Alicerce Romano",
    subtitle: "Por que sua fé oscila — e o que a Escritura oferece como fundamento",
    introduction: "O seu cansaço espiritual não é falta de oração. É um erro de engenharia na base da sua fé.",
    mainThesis: "Durante muito tempo, fomos ensinados a sustentar a vida cristã sobre a areia movediça das emoções — o arrepio do louvor, o choro no congresso, o fervor momentâneo de um evento. O problema é que as emoções são como fogo de palha: queimam forte, mas esfriam na segunda-feira.",
    coreStatement: "O Alicerce Romano substitui essa base frágil pela estrutura de concreto da Fé Racional. Através da lógica da Epístola aos Romanos, nós não buscamos um sentimento novo a cada manhã — nós instalamos uma convicção inabalável na mente que sustenta o seu espírito mesmo nos dias mais secos e difíceis.",
    whyCurrentSolutionsFail: {
      title: "Por que as soluções atuais não funcionam",
      points: [
        {
          title: "Devocionais de conforto rápido",
          description: "Oferecem mensagens agradáveis e frases reconfortantes, mas não constroem raízes. Você se torna dependente de uma dose de ânimo que não sustenta o peso das lutas diárias."
        },
        {
          title: "A montanha-russa de eventos",
          description: "Geram picos de emoção e euforia. Quando o som desliga e a luz apaga, você volta para casa sentindo-se culpado por não sentir mais a presença de Deus. Um ciclo de condenação espiritual."
        },
        {
          title: "O ruído do entretenimento gospel",
          description: "Tenta combater o cansaço do mundo com mais barulho — mais vídeos, mais telas, mais estímulos. Isso apenas aumenta a fadiga mental e impede a verdadeira meditação na Palavra."
        }
      ]
    },
    whyThisWorks: {
      title: "Por que o Alicerce Romano funciona",
      description: "Porque ataca a raiz: a frieza espiritual por falta de fundamento. Em vez de tentar fazer você sentir algo, o método Vivos no Espírito utiliza a epístola mais profunda e lógica da Bíblia para realizar uma renovação da mente.",
      keyPoint: "Quando você entende racionalmente a sua Justificação e a vitória de Cristo, sua paz deixa de ser um arrepio e passa a ser uma Rocha. Você para de oscilar conforme o seu humor e passa a caminhar conforme a Verdade imutável."
    },
    evidence: [
      "A Epístola aos Romanos foi o instrumento que tirou a Igreja do automático nos momentos mais cruciais da história — na Reforma de Lutero e no despertar de Wesley. É o livro que transforma teologia em vida prática.",
      "Parafraseando o arqueólogo Rodrigo Silva: a fé, para ser madura, precisa passar pelo crivo da razão. Caso contrário, torna-se fideísmo — uma crença cega e frágil que desmorona diante de qualquer dúvida ou sofrimento real.",
      "Em tempos de exaustão digital, a leitura profunda e o silêncio reativam a capacidade de concentração e escuta espiritual — o antídoto para o que o mundo chama de brain rot."
    ]
  },

  solution: {
    title: "Vivos no Espírito",
    subtitle: "Uma jornada bíblica, pastoral e devocional na Epístola aos Romanos",
    introduction: "Vivos no Espírito é uma jornada devocional e teológica de 14 dias, construída sobre a Epístola de Paulo aos Romanos, com o objetivo de ajudar cristãos a viverem a fé de forma consciente, bíblica e aplicada ao cotidiano — sem superficialidade, sem moralismo e sem modismos espirituais.",
    keyPhrase: "Um convite simples e profundo: voltar à Palavra, ouvir o Espírito e viver a fé como ela sempre foi vivida.",
    existentialNote: "A fé cristã não foi dada apenas para ser confessada. Foi dada para ser vivida — com intenção, com consciência, com raízes.",
    whatItIs: {
      title: "O que é",
      items: [
        "Uma jornada de 14 dias guiada pela Epístola aos Romanos",
        "Reflexões teológicas claras, pastorais e aplicáveis ao cotidiano",
        "Estrutura fixa de manhã (leitura e reflexão) e noite (contemplação)",
        "Material escrito — sem vídeos, sem distrações, sem ruído digital",
        "Linguagem reverente, bíblica e acessível para qualquer cristão"
      ]
    },
    whatItIsNot: {
      title: "O que não é",
      items: [
        "Não é um curso de teologia acadêmica nem um seminário condensado",
        "Não promete experiências sobrenaturais instantâneas",
        "Não é motivacional sem fundamento bíblico",
        "Não substitui a igreja local nem o discipulado pastoral",
        "Não é mais um devocional de frases bonitas sem profundidade"
      ]
    },
    howItWorks: {
      title: "Como funciona na prática",
      schedule: "Alguns minutos pela manhã. Alguns minutos à noite.",
      format: "Formato: texto escrito (PDF) — leitura individual, familiar ou em pequenos grupos.",
      structure: [
        "Cada dia começa com o resumo da lição anterior",
        "Introdução pastoral com incentivação",
        "Texto bíblico de Romanos com desenvolvimento guiado",
        "Aplicação prática imediata",
        "Contemplação noturna para encerrar o dia"
      ],
      practical: "Cabe na rotina de quem trabalha, cuida da família e serve na igreja. Não exige horas livres. Exige atenção."
    }
  },

  forWhom: {
    forTitle: "Este material é para você se:",
    forList: [
      "Você crê em Cristo e respeita as Escrituras, mas sente que sua fé está mecânica — como uma rotina que perdeu o sentido.",
      "Você está cansado da montanha-russa espiritual — dos altos e baixos que dependem de eventos, cultos ou emoções passageiras.",
      "Você deseja clareza teológica para enfrentar as lutas reais da vida — no trabalho, em casa, nas decisões difíceis.",
      "Você quer ser capaz de conduzir sua família espiritualmente, com segurança e fundamento bíblico.",
      "Você busca crescimento espiritual consistente, não experiências isoladas que evaporam em poucos dias."
    ],
    notForTitle: "Este material não é para você se:",
    notForList: [
      "Você busca experiências espirituais intensas sem compromisso com o estudo da Palavra.",
      "Você quer motivação instantânea, mas não está disposto a dedicar tempo diário em silêncio e reflexão.",
      "Você procura soluções rápidas para problemas espirituais complexos — uma fórmula de transformação.",
      "Você não tem interesse em examinar a Epístola aos Romanos com seriedade e reverência.",
      "Você não está disposto a confrontar suas próprias escolhas à luz das Escrituras."
    ]
  },

  differentiation: {
    title: "Por que este caminho é diferente",
    introduction: "Existem muitos recursos disponíveis para o cristão que busca crescimento. Nem todos são iguais. Nem todos levam ao mesmo lugar.",
    comparisons: [
      {
        category: "Devocionais emocionais",
        description: "Oferecem frases reconfortantes e leituras rápidas. Mas não fornecem a base teológica necessária para enfrentar crises reais de fé, dúvidas profundas ou o desgaste do cotidiano. Confortam, mas não fundamentam.",
        contrast: "Vivos no Espírito não busca confortar — busca fundamentar. A paz que ele oferece não vem de uma frase bonita, mas da compreensão da doutrina."
      },
      {
        category: "Cursos teológicos acadêmicos",
        description: "Oferecem rigor exegético e profundidade intelectual — e são de enorme valor para a formação cristã. Porém, por vezes, a rotina intensa de trabalho e família não permite que muitos cristãos dediquem o tempo necessário a esse tipo de conteúdo mais extenso e aprofundado.",
        contrast: "Vivos no Espírito pode ser uma porta de entrada para despertar o interesse pela teologia séria. Oferece profundidade acessível, respeitando a inteligência do leitor, e pode gerar o desejo de ir mais fundo através de cursos e estudos formais."
      },
      {
        category: "Espiritualidade de eventos",
        description: "Congressos, retiros e encontros geram momentos de comunhão e fervor. Mas não podem criar uma dependência do ambiente para se sentir próximo de Deus. Quando o evento acaba, a fé precisa sobreviver na rotina — e muitas vezes não sobrevive.",
        contrast: "Vivos no Espírito foi desenhado para a rotina, não para o evento. Funciona na segunda-feira de manhã, não apenas no sábado à noite."
      }
    ]
  },

  productDemo: {
    title: "O ambiente de estudo",
    subtitle: "Onde você vai acessar o material",
    description: "Vivos no Espírito está disponível dentro da Comunidade A,Ê! — um ambiente digital organizado, sóbrio e livre de distrações.",
    features: [
      "Interface limpa e respeitosa, sem notificações excessivas",
      "Trilhas estruturadas: cada material em seu lugar, cada dia em sua ordem",
      "Acesso ao conteúdo principal e aos cadernos complementares no mesmo ambiente",
      "Projetado para leitura profunda — não para consumo rápido"
    ],
    closingNote: "O ambiente foi pensado para que sua experiência de estudo seja tão sóbria e reverente quanto o próprio conteúdo. Um espaço digital que respeita o seu tempo de silêncio com Deus.",
    visualization: {
      morning: "Imagine: a manhã ainda está quieta. Você abre o material. Sem notificações, sem pressa, sem ruído competindo pela sua atenção. Apenas a Palavra, o silêncio e o seu tempo. É assim que começa cada dia da jornada.",
      evening: "À noite, antes de dormir, você abre o Caderno de Reflexão. Não há pressa. Algumas perguntas simples. Um exame do dia à luz do que foi lido pela manhã. E uma oração. O dia termina com consciência — não com telas."
    }
  },

  bonus: {
    title: "Companheiros de jornada",
    subtitle: "Incluídos no seu acesso",
    items: [
      {
        name: "Caderno de Aplicação Imediata",
        description: "Perguntas práticas para aplicar a fé ao dia. Cada manhã, após a leitura do texto bíblico, o Caderno guia você a conectar a doutrina às suas decisões reais — no trabalho, em casa, nos relacionamentos. Não é teoria. É a Palavra encontrando a vida.",
        icon: "notebook"
      },
      {
        name: "Caderno de Reflexão Noturna",
        description: "Reflexão, exame pessoal e oração ao final do dia. Antes de dormir, alguns minutos para olhar para trás e perguntar: o que Deus me mostrou hoje? Onde eu fui fiel? Onde preciso de graça? Um companheiro silencioso para encerrar o dia com consciência.",
        icon: "moon"
      }
    ],
    note: "Estes cadernos não são simples bônus de marketing. São instrumentos pastorais desenhados para que a jornada não termine na leitura — mas continue na vida."
  },

  product: {
    name: "Vivos no Espírito",
    subtitle: "14 dias em Romanos para alinhar sua vida à Palavra e ao Espírito",
    price: "R$ 27,00",
    investmentNote: "Um investimento na sua vida espiritual",
    ctaText: "Quero começar a jornada",
    paymentNote: "Pagamento seguro via Hotmart — acesso imediato após confirmação"
  },

  credibility: {
    title: "Os compromissos que sustentam este material",
    introduction: "Todo material cristão carrega uma responsabilidade: honrar a Palavra e servir ao leitor com verdade.",
    points: [
      {
        title: "Fidelidade às Escrituras",
        description: "Cada reflexão está ancorada no texto de Romanos. Não há interpretações forçadas, confissão positiva, teologia da prosperidade ou adaptações convenientes. A Escritura governa o tom, o ritmo e o conteúdo."
      },
      {
        title: "Respeito à fé histórica",
        description: "Este material se posiciona dentro da tradição cristã evangélica histórica. Não busca inovação teológica, mas fidelidade. Não busca velocidade, mas constância."
      },
      {
        title: "Honestidade pastoral",
        description: "Não prometemos transformação mágica. Não oferecemos soluções instantâneas. Oferecemos um caminho bíblico e deixamos o Espírito Santo agir. A transformação vem dEle — o material é apenas um instrumento."
      },
      {
        title: "Rejeição da superficialidade",
        description: "Num tempo de conteúdo rápido e espiritualidade apressada, este material escolhe a profundidade. Escolhe o silêncio. Escolhe a leitura atenta. Porque a fé cristã nunca foi feita para ser consumida — foi feita para ser vivida."
      }
    ]
  },

  faq: [
    {
      question: "Preciso ter conhecimento teológico avançado?",
      answer: "Não. O material foi escrito para cristãos comuns — pessoas que creem em Cristo, respeitam a Bíblia e desejam crescer. A linguagem é clara e pastoral. Você não precisa ser teólogo. Precisa apenas estar disposto a ler com atenção e refletir com honestidade."
    },
    {
      question: "Quanto tempo por dia preciso dedicar?",
      answer: "Alguns minutos pela manhã para a leitura e reflexão, e alguns minutos à noite para a contemplação. É um tempo que cabe na rotina real de quem trabalha, cuida da família e serve na igreja. A constância importa mais do que a duração."
    },
    {
      question: "Romanos não é um livro muito difícil?",
      answer: "Romanos é profundo, mas não inacessível. O material guia você dia a dia, versículo por versículo, com explicações claras que respeitam o texto bíblico sem simplificar a mensagem. Você não estará sozinho na leitura — estará sendo conduzido."
    },
    {
      question: "Estou espiritualmente cansado. Isso vai funcionar para mim?",
      answer: "Este material foi pensado exatamente para quem está cansado da rotina espiritual mecânica. Não é mais uma promessa de avivamento instantâneo. É um caminho bíblico, passo a passo, que respeita o seu ritmo e confia que o Espírito Santo age através da Palavra — mesmo nos dias mais secos."
    },
    {
      question: "E se eu não conseguir manter a constância nos 14 dias?",
      answer: "A vida é real, e o material respeita isso. Se precisar pausar um dia, retome no seguinte. O objetivo é crescimento, não perfeição. Deus é paciente com você. Este material também é."
    },
    {
      question: "Isso vai resolver todos os meus problemas espirituais?",
      answer: "Não. E qualquer material que prometa isso não merece sua confiança. O que Vivos no Espírito pode oferecer é clareza, direção e um fundamento bíblico sólido para a sua caminhada. A transformação é obra do Espírito. Nós apenas colocamos a Palavra diante dos seus olhos."
    },
    {
      question: "E se eu não gostar ou sentir que não é para mim?",
      answer: "Você tem 7 dias de garantia incondicional. Se sentir que o material não é adequado para a sua caminhada, basta solicitar o reembolso. Sem perguntas, sem constrangimento. Queremos que você tenha paz na decisão — antes, durante e depois."
    }
  ],

  guarantee: {
    title: "Garantia de 7 dias",
    description: "Se por qualquer motivo você sentir que este material não é para a sua caminhada, basta solicitar o reembolso em até 7 dias. Devolvemos seu investimento integralmente.",
    emphasis: "Sem perguntas. Sem julgamentos. Sem constrangimento.",
    pastoralNote: "Não queremos que você tome esta decisão por impulso. Queremos que você decida com clareza e paz. Se depois de experimentar o material você entender que não é o momento, respeitamos isso com a mesma seriedade com que o criamos."
  },

  finalCTA: {
    title: "Um convite à maturidade",
    paragraphs: [
      "Existe um momento na caminhada cristã em que a fé precisa deixar de ser apenas confissão e passar a ser fundamento. Não por obrigação. Não por culpa. Mas por maturidade.",
      "Vivos no Espírito não promete uma experiência extraordinária. Promete algo mais raro: um caminho ordinário, fiel e sustentável de comunhão com Deus através da Sua Palavra.",
      "Se você chegou até aqui, talvez seja porque algo dentro de você reconhece que é hora de construir sobre a Rocha. Não sobre a emoção do momento. Não sobre a empolgação do evento. Sobre a Verdade que permanece."
    ],
    invitation: "Este é um convite. Calmo. Sério. Pastoral.",
    closingVerse: "\u201CPortanto, todo aquele que ouve estas minhas palavras e as pratica é como um homem prudente que construiu a sua casa sobre a rocha.\u201D — Mateus 7:24",
    buttonText: "Quero construir sobre a Rocha",
    afterButton: "Acesso imediato após a confirmação do pagamento",
    blessing: "Que o Senhor o conduza com sabedoria e firmeza. E que a Sua Palavra seja lâmpada para os seus pés e luz para o seu caminho."
  },

  authority: {
    paragraphs: [
      "Acorda, Êutico! não é um perfil pessoal, um projeto emocional ou um ministério de performance.",
      "É um projeto cristão institucional — um chamado à vigilância bíblica, à consciência espiritual e à aplicação prática das Escrituras na vida real.",
      "O nome nasce de Atos 20:9. Êutico estava presente na reunião. Ouvia a Palavra sendo pregada. Mas não estava atento — e adormeceu. Não por rebeldia. Por distração. E caiu.",
      "Esse retrato resume uma realidade silenciosa da fé contemporânea: muita informação, pouca vigilância. Muita atividade, pouca consciência espiritual. Muita presença nos bancos da igreja, pouca atenção ao que está sendo dito.",
      "O propósito deste projeto é simples e antigo: conduzir almas a Deus através da Palavra. Despertar a consciência cristã — não apenas a emoção. Promover um estudo bíblico sério, acessível e fiel. E ajudar cristãos comuns a alinhar fé, identidade e decisões cotidianas.",
      "A teologia aqui é aplicada. Não fria e acadêmica. Nem rasa e devocional. Comprometida com a autoridade das Escrituras. Livre de disputas denominacionais. Avessa à superficialidade, ao moralismo e ao sensacionalismo.",
      "Vivos no Espírito nasce como extensão natural deste compromisso — um material pastoral para quem deseja profundidade sem pressa, fidelidade sem rigidez, e crescimento sem espetáculo."
    ],
    closingLine: "Um púlpito silencioso no meio do ruído digital."
  },

  footer: {
    brand: "Acorda, Êutico!",
    tagline: "Um chamado ao despertar espiritual. Não um evento. Uma jornada.",
    copyright: "\u00A9 2025 Acorda, Êutico! Todos os direitos reservados.",
    contact: "contato@acordaeutico.com.br"
  }
};
